import { alpha, Box, Typography, useTheme } from "@mui/material";

import type { ApplicationCategoryStatusView, ResolvedStatus } from "../dtos";

/**
 * §CaseStatus: surfaces the workflow's case-level status to the agent in the CRM tab.
 *
 * WM knows the verdict (e.g. "Credit Declined", "Credit Proposal Sent", "Approved") and already
 * serves it on the case view as `statusBadge` — but the CRM tab wasn't rendering it, so an agent
 * couldn't tell a declined/cancelled application apart from one still in progress. This pill mirrors
 * WM's own top-of-case status.
 *
 * Tone is classified from the status key (falling back to the lifecycle status) onto the design
 * system's semantic extension tokens rather than the raw hex WM sends, so it themes correctly.
 */
// "disabled" is the design system's neutral/grey extension tone (there is no "neutral" token).
type Tone = "negative" | "positive" | "warning" | "disabled";

const toneFor = (statusKey: string | null | undefined, caseStatus: string): Tone => {
  const key = (statusKey ?? "").toLowerCase();
  const lifecycle = (caseStatus ?? "").toUpperCase();
  if (/declin|reject|cancel/.test(key) || lifecycle === "CANCELLED") return "negative";
  // "conditional-approval" must be caught before the plain "approv" check below.
  if (/conditional|pending|review|proposal/.test(key)) return "warning";
  if (/approv|complet|qualif|booked|closed|won/.test(key) || lifecycle === "COMPLETED") return "positive";
  return "disabled";
};

/** WM accepts CSS color strings beyond MUI alpha's parser, including named colors. */
const categoryBackground = (color: string | null | undefined, fallback: string): string => {
  if (!color) return fallback;
  try {
    const tinted = alpha(color, 0.15);
    // Malformed hex/function values can parse without throwing but yield invalid numeric channels.
    return /NaN|Infinity/.test(tinted) ? fallback : tinted;
  } catch {
    return fallback;
  }
};

/** Empty authored strings follow the same fallback path as missing values. */
const authoredValue = (value: string | null | undefined): string | undefined =>
  value === "" ? undefined : value ?? undefined;

const CaseStatusBadge = ({
  statusBadge,
  caseStatus,
  categoryStatuses,
}: {
  statusBadge: ResolvedStatus | null;
  caseStatus: string;
  categoryStatuses?: ApplicationCategoryStatusView[];
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;

  if (categoryStatuses) return (
    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0, maxWidth: "100%"}}>
      {categoryStatuses.map(({category, status}) => {
        // Authored category values are informational; their text has no lifecycle/decision meaning.
        const color = authoredValue(status?.color) ?? colors.semantic.text.secondary;
        const label = `${category.name}: ${authoredValue(status?.label) ?? authoredValue(status?.statusKey) ?? "Not set"}`;
        return <Box key={category.key} sx={{px: 0.75, py: "2px", minWidth: 0, maxWidth: "100%",
          borderRadius: theme.nawy.radius.md,
          backgroundColor: categoryBackground(status?.color, colors.semantic.layer.secondary ?? theme.palette.background.paper)}}>
          <Typography sx={{fontSize: 11, lineHeight: "18px", fontWeight: 600, color,
            whiteSpace: "normal", overflowWrap: "anywhere"}}>{label}</Typography>
        </Box>;
      })}
    </Box>
  );

  // WM emits a badge only once the case carries a meaningful status_key; absent that (early
  // onboarding) the stepper + next-action banner already convey progress, so show nothing —
  // unless the case is in a terminal lifecycle state that must never be silent.
  const terminal = ["CANCELLED", "COMPLETED"].includes((caseStatus ?? "").toUpperCase());
  const label = statusBadge?.label ?? (terminal ? caseStatus : null);
  if (!label) return null;

  const tone = toneFor(statusBadge?.key, caseStatus);
  const bg = colors.semantic.layer.extension[`${tone}Light` as const];
  const fg = colors.semantic.text.extension[tone];

  return (
    <Box
      sx={{
        px: 1,
        py: "2px",
        borderRadius: theme.nawy.radius.md,
        backgroundColor: bg,
        display: "inline-flex",
        alignItems: "center",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Typography
        sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 600, color: fg, whiteSpace: "normal", overflowWrap: "anywhere" }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default CaseStatusBadge;
